import { useContext, useMemo, useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useLocation, useParams } from 'react-router-dom';
import queryString from 'query-string';
import getRoute from '../utils/getRoute';
// eslint-disable-next-line import/no-cycle
import { Context } from '../context/AppContext';

const useSsr = ssr => {
  const { pathname, search } = useLocation();
  const params = useParams();
  const route = getRoute(pathname);

  const [apiData, setApiData] = useState({});
  const [loading, setLoading] = useState();
  const client = useApolloClient();

  const { isServer, cookie, userAgent: { userAgent } = {}, ...context } =
    useContext(Context) || {};

  const apiArray = useMemo(() => {
    const query = queryString.parse(search || '');

    return Promise.resolve(
      ssr({
        params,
        query,
        pathname,
        cookies: cookie.getCookieAll(),
        redirect: url => ({ isRedirect: !!url, url }),
        userAgent,
      })
    );
  }, [pathname, search]);

  const fetch = async ({
    name,
    query,
    variables,
    options,
    callback = () => false,
  }) => {
    const results = await client.query({
      query,
      ...options,
      variables,
    });
    const { data, errors } = results || {};

    const callbackData = await callback(data, errors);

    return {
      type: name,
      redirectUrl:
        callbackData && callbackData.isRedirect ? callbackData.url : false,
      data: callbackData && callbackData.data ? callbackData.data : data,
      errors,
    };
  };

  const apiResolve = async arr => {
    setLoading(true);
    const promiseArray = arr.map(
      async ({ name, query, variables, options, callback = () => false }) => {
        return fetch({ name, query, variables, options, callback });
      }
    );

    const resolved = await Promise.all(promiseArray);

    const results = resolved.reduce(
      (acc, { type, data, errors, redirectUrl }) => ({
        ...(acc || {}),
        [type]: { ...data, errors },
        ...(redirectUrl ? { redirectUrl } : {}),
      }),
      {}
    );
    setApiData(results);
    setLoading(false);
  };

  useEffect(() => {
    if (isServer) {
      return;
    }
    apiArray.then(res => apiResolve(res));
  }, [apiArray]);

  const routeData = context[route] && context[route].data;

  return { ...routeData, ...apiData, loading };
};

export default useSsr;
